<script lang="ts" setup>
    import {ref, onMounted, onUnmounted, computed} from "vue";
    import emblaCarouselVue from "embla-carousel-vue";

    import DButton from "../atoms/DButton.vue";
    import DContainer from "../atoms/DContainer.vue";

    import {getComponentShade} from "../utils/theme";

    import type {EmblaOptionsType} from "embla-carousel";
    import type {ComponentThemes} from "@songfinch/types";

    const props = withDefaults(
        defineProps<{
            dataTestId: string;
            options?: EmblaOptionsType;
            smoothScroll?: boolean;
            withNavigation?: boolean;
            vAlign?: "center" | "start" | "stretch" | "";
            variant?: "flush";
            theme?: ComponentThemes;
            parentStyleClass?: string;
        }>(),
        {
            dataTestId: "DCarousel",
            smoothScroll: false,
            withNavigation: false,
            theme: "light",
            vAlign: "",
            parentStyleClass: "",
        },
    );

    const intervalId = ref<number | null>(null);
    const dots = ref<number[]>([]);
    const selectedIndex = ref<number>(0);

    const shade = computed(() => getComponentShade(props.theme));

    const emit = defineEmits(["carouselScrolled", "carouselDragged"]);

    const optionDefaults: EmblaOptionsType = {
        loop: true,
        align: "center",
        dragFree: true,
        containScroll: "trimSnaps",
        skipSnaps: false,
    };

    const [emblaRef, emblaApi] = emblaCarouselVue({
        ...optionDefaults,
        ...props.options,
    });

    const updateDots = () => {
        if (!emblaApi.value) return;

        const embla = emblaApi.value;
        const slides = embla.slideNodes();

        if (!slides) return;

        dots.value = Array.from({length: slides.length}, (_, i) => i);
        selectedIndex.value = embla.selectedScrollSnap();
    };

    const scrollCarousel = (dir: "prev" | "next", e: MouseEvent) => {
        if (dir === "prev") {
            emblaApi.value?.scrollPrev();
        } else {
            emblaApi.value?.scrollNext();
        }
        emit("carouselScrolled", e, "carousel_scrolled", {action: "nav_clicked"});
    };

    const touchScroll = () => {
        emit("carouselDragged", emblaRef.value, "carousel_scrolled", {
            action: "touch_scroll",
        });
    };

    onMounted(() => {
        if (emblaApi.value) {
            emblaApi.value.on("pointerDown", touchScroll);

            if (props.withNavigation) {
                emblaApi.value.on("select", updateDots);
                emblaApi.value.on("init", updateDots);
            }

            if (props.smoothScroll) {
                intervalId.value = setInterval(() => {
                    if (props.smoothScroll) {
                        emblaApi.value?.scrollNext();
                    }
                }, 3000) as unknown as number;
            }
        }
    });

    onUnmounted(() => {
        if (intervalId.value !== null) {
            clearInterval(intervalId.value);
        }
    });
</script>

<template>
    <div ref="emblaRef" class="embla" :data-test-id="`${dataTestId}`">
        <div
            class="embla__container"
            :class="`${vAlign} ${variant}`"
            v-bind="$attrs"
        >
            <slot/>
        </div>
    </div>
    <div v-if="withNavigation" class="embla__dots" :class="shade">
        <div
            v-for="(dot, index) in dots"
            :key="index"
            class="embla__dot"
            :class="{'embla__dot-selected': selectedIndex === index}"
        >
            <svg
                width="9"
                height="8"
                viewBox="0 0 9 8"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
            >
                <circle cx="4.5" cy="4" r="4" fill="currentColor"/>
            </svg>
        </div>
    </div>
    <div v-if="withNavigation" class="navigation-container" :class="{'navigation-container-with-content': $slots['nav-content']}">
        <DContainer v-if="$slots['nav-content']">
            <slot name="nav-content"/>
        </DContainer>
        <DContainer class="navigation-button-container">
            <DButton
                icon="arrow_left_md"
                :theme="theme"
                variant="circle-outline"
                :data-test-id="`${dataTestId}-scroll-prev`"
                data-test
                aria-label="Previous Slide"
                @click="scrollCarousel('prev', $event)"
            />
            <DButton
                icon="arrow_right_md"
                :data-test-id="`${dataTestId}-scroll-next`"
                :theme="theme"
                variant="circle-outline"
                aria-label="Next Slide"
                @click="scrollCarousel('next', $event)"
            />
        </DContainer>
    </div>
</template>

<style lang="scss" scoped>
.embla {
  overflow: hidden;
  width: 100%;
}

.embla__container {
  display: flex;
  column-gap: var(--carousel-card-gap);
  padding-inline: var(--carousel-card-gap);
  height: 100%;

  &.flush {
    column-gap: initial;
    padding-inline: initial;
  }

  &.stretch {
    align-items: stretch;
  }

  &.center {
    align-items: center;
  }

  &.start {
    align-items: flex-start;
  }
}

.embla__dots {
  display: flex;
  justify-content: center;
  padding-block: var(--size-24);

  @include md {
    display: none;
  }

  .embla__dot {
    appearance: none;
    background-color: transparent;
    border: none;
    margin-inline: 3px;
    padding: 0;
    text-align: inherit;
    font: inherit;
    border-radius: var(--radius-none);
  }

  &.light {
    .embla__dot {
      color: var(--soft-black-tint-20);
    }

    .embla__dot-selected {
      color: var(--soft-black);
    }
  }

  &.dark {
    .embla__dot {
      color: var(--white-tint-20);
    }

    .embla__dot-selected {
      color: var(--white);
    }
  }
}

.navigation-container {
  align-items: flex-start;
  max-width: 1440px;
  justify-content: center;
  margin-top: 1.25rem;

  @include md {
    display: flex;
    column-gap: 4px;
    margin-top: 4rem;
    padding-block: 24px;
  }
}

.navigation-container-with-content {
  @include md {
    display: grid;
    justify-content: space-between;
    column-gap: 2rem;
    grid-template-columns: minmax(auto, 770px) auto;
  }
}

.navigation-button-container {
    display: none;

  @include md {
    display: flex;
    column-gap: 4px;
  }
}
</style>
